import { useState, useEffect } from 'react';
import { getAdminReviews, createAdminReview, updateAdminReview, deleteAdminReview, getSiteSettings, updateSiteSettings, getAdminCampaigns } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Star, Eye, EyeOff } from 'lucide-react';

const emptyForm = { campaign_id: '', user_name: '', rating: 5, text: '', is_visible: true };

export default function AdminReviews() {
  const [settings, setSettings] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [filterCampaign, setFilterCampaign] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [s, r, c] = await Promise.all([
        getSiteSettings(),
        getAdminReviews(filterCampaign),
        getAdminCampaigns(),
      ]);
      setSettings(s.data);
      setReviews(r.data);
      setCampaigns(c.data);
    } catch (e) {
      toast.error('Errore caricamento dati');
    }
    setLoading(false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [filterCampaign]);

  const handleToggleReviews = async (enabled) => {
    try {
      const res = await updateSiteSettings({ reviews_enabled: enabled });
      setSettings(res.data);
      toast.success(enabled ? 'Recensioni visibili sul sito' : 'Recensioni nascoste dal sito');
    } catch {
      toast.error('Errore aggiornamento');
    }
  };

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...emptyForm, campaign_id: filterCampaign || (campaigns[0]?.campaign_id ?? '') });
    setDialogOpen(true);
  };

  const openEdit = (rev) => {
    setEditingId(rev.review_id);
    setForm({
      campaign_id: rev.campaign_id,
      user_name: rev.user_name || '',
      rating: rev.rating || 5,
      text: rev.text || '',
      is_visible: rev.is_visible !== false,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.user_name.trim() || !form.text.trim() || !form.campaign_id) {
      toast.error('Compila tutti i campi obbligatori');
      return;
    }
    try {
      if (editingId) {
        await updateAdminReview(editingId, {
          user_name: form.user_name, rating: Number(form.rating),
          text: form.text, is_visible: form.is_visible,
        });
        toast.success('Recensione aggiornata');
      } else {
        await createAdminReview({ ...form, rating: Number(form.rating) });
        toast.success('Recensione creata');
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore salvataggio');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questa recensione?')) return;
    try {
      await deleteAdminReview(id);
      toast.success('Recensione eliminata');
      load();
    } catch {
      toast.error('Errore eliminazione');
    }
  };

  const toggleVisibility = async (rev) => {
    try {
      await updateAdminReview(rev.review_id, { is_visible: !(rev.is_visible !== false) });
      load();
    } catch {
      toast.error('Errore aggiornamento');
    }
  };

  const campaignTitle = (cid) => campaigns.find(c => c.campaign_id === cid)?.title || '-';

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: 'var(--accent)', borderTopColor: 'transparent' }} /></div>;

  return (
    <div className="space-y-6" data-testid="admin-reviews-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl tracking-tight mb-1" style={{ fontFamily: 'var(--font-heading)', color: 'var(--text-primary)', fontWeight: 600 }}>Recensioni</h1>
          <p className="font-body text-sm" style={{ color: 'var(--text-muted)', fontFamily: 'var(--font-body)' }}>Gestisci le recensioni mostrate sul microsite</p>
        </div>
        <Button onClick={openCreate} data-testid="new-review-btn" className="rounded-sm" style={{ backgroundColor: 'var(--accent)', color: '#fff' }}>
          <Plus className="w-4 h-4 mr-2" />Nuova
        </Button>
      </div>

      {/* Global toggle */}
      <Card className="border rounded-sm" style={{ borderColor: 'var(--border-light)' }}>
        <CardContent className="p-5 flex items-center justify-between gap-4">
          <div>
            <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Mostra recensioni sul sito pubblico</p>
            <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
              {settings?.reviews_enabled ? 'Le recensioni sono attualmente visibili a tutti i visitatori' : 'La sezione recensioni è nascosta dal sito'}
            </p>
          </div>
          <Switch
            checked={!!settings?.reviews_enabled}
            onCheckedChange={handleToggleReviews}
            data-testid="reviews-enabled-toggle"
          />
        </CardContent>
      </Card>

      {/* Filter */}
      <div className="flex items-center gap-3">
        <Label className="text-xs">Filtra per prodotto:</Label>
        <select
          value={filterCampaign}
          onChange={e => setFilterCampaign(e.target.value)}
          className="border rounded-sm px-3 py-1.5 text-sm bg-white"
          style={{ borderColor: 'var(--border-light)' }}
          data-testid="filter-campaign-select"
        >
          <option value="">Tutti i prodotti</option>
          {campaigns.map(c => (
            <option key={c.campaign_id} value={c.campaign_id}>{c.title}</option>
          ))}
        </select>
        <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{reviews.length} recensioni</span>
      </div>

      {/* Reviews list */}
      <div className="space-y-3">
        {reviews.length === 0 && (
          <Card className="border rounded-sm" style={{ borderColor: 'var(--border-light)' }}>
            <CardContent className="p-8 text-center">
              <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Nessuna recensione. Crea la prima recensione cliccando "Nuova"</p>
            </CardContent>
          </Card>
        )}
        {reviews.map((r) => {
          const hidden = r.is_visible === false;
          return (
            <Card key={r.review_id} className="border rounded-sm" style={{ borderColor: 'var(--border-light)', opacity: hidden ? 0.6 : 1 }} data-testid={`review-card-${r.review_id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{r.user_name}</p>
                      <div className="flex">
                        {[1, 2, 3, 4, 5].map(n => (
                          <Star key={n} className="w-3.5 h-3.5" fill={n <= r.rating ? '#FACC15' : 'transparent'} stroke={n <= r.rating ? '#FACC15' : '#D1D5DB'} />
                        ))}
                      </div>
                      {r.is_admin_created && <Badge variant="outline" className="text-[10px] rounded-sm" style={{ borderColor: 'var(--border-light)', color: 'var(--text-muted)' }}>fittizia</Badge>}
                      {hidden && <Badge variant="outline" className="text-[10px] rounded-sm" style={{ borderColor: '#fecaca', color: '#dc2626' }}>nascosta</Badge>}
                    </div>
                    <p className="text-xs mb-2" style={{ color: 'var(--text-muted)' }}>{campaignTitle(r.campaign_id)}</p>
                    <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{r.text}</p>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <button onClick={() => toggleVisibility(r)} title={hidden ? 'Rendi visibile' : 'Nascondi'} data-testid={`toggle-visibility-${r.review_id}`}
                      className="p-1.5 rounded-sm hover:bg-gray-100">
                      {hidden ? <EyeOff className="w-4 h-4" style={{ color: 'var(--text-muted)' }} /> : <Eye className="w-4 h-4" style={{ color: 'var(--text-muted)' }} />}
                    </button>
                    <button onClick={() => openEdit(r)} title="Modifica" data-testid={`edit-review-${r.review_id}`}
                      className="p-1.5 rounded-sm hover:bg-gray-100">
                      <Pencil className="w-4 h-4" style={{ color: 'var(--text-muted)' }} />
                    </button>
                    <button onClick={() => handleDelete(r.review_id)} title="Elimina" data-testid={`delete-review-${r.review_id}`}
                      className="p-1.5 rounded-sm hover:bg-red-50">
                      <Trash2 className="w-4 h-4" style={{ color: '#dc2626' }} />
                    </button>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Modifica recensione' : 'Nuova recensione'}</DialogTitle>
            <DialogDescription>{editingId ? 'Aggiorna i campi della recensione' : 'Inserisci i dettagli della recensione'}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Prodotto *</Label>
              <select
                value={form.campaign_id}
                onChange={e => setForm(f => ({ ...f, campaign_id: e.target.value }))}
                className="w-full mt-1 border rounded-sm px-3 py-2 text-sm bg-white"
                style={{ borderColor: 'var(--border-light)' }}
                disabled={!!editingId}
                data-testid="form-campaign-select"
              >
                <option value="">Seleziona prodotto</option>
                {campaigns.map(c => (
                  <option key={c.campaign_id} value={c.campaign_id}>{c.title}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-xs">Nome autore *</Label>
              <Input value={form.user_name} onChange={e => setForm(f => ({ ...f, user_name: e.target.value }))}
                placeholder="Es. Dr. Marco Rossi" className="mt-1 rounded-sm text-sm" data-testid="form-author-name" />
            </div>
            <div>
              <Label className="text-xs">Valutazione (1-5) *</Label>
              <div className="flex gap-2 mt-1">
                {[1, 2, 3, 4, 5].map(n => (
                  <button key={n} type="button" onClick={() => setForm(f => ({ ...f, rating: n }))}
                    className="p-1" data-testid={`form-rating-${n}`}>
                    <Star className="w-6 h-6" fill={n <= form.rating ? '#FACC15' : 'transparent'} stroke={n <= form.rating ? '#FACC15' : '#D1D5DB'} />
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs">Testo recensione *</Label>
              <textarea
                value={form.text}
                onChange={e => setForm(f => ({ ...f, text: e.target.value }))}
                rows={4}
                placeholder="Testo della recensione..."
                className="w-full mt-1 border rounded-sm px-3 py-2 text-sm"
                style={{ borderColor: 'var(--border-light)' }}
                data-testid="form-review-text"
              />
            </div>
            <div className="flex items-center justify-between p-3 rounded-sm" style={{ backgroundColor: 'var(--bg-warm)' }}>
              <div>
                <p className="text-xs font-medium" style={{ color: 'var(--text-primary)' }}>Visibile sul sito</p>
                <p className="text-[11px]" style={{ color: 'var(--text-muted)' }}>Disattiva per nascondere senza eliminare</p>
              </div>
              <Switch checked={form.is_visible} onCheckedChange={v => setForm(f => ({ ...f, is_visible: v }))} data-testid="form-is-visible" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-sm" data-testid="cancel-review-btn">Annulla</Button>
              <Button onClick={handleSave} data-testid="save-review-btn" className="rounded-sm" style={{ backgroundColor: 'var(--accent)', color: '#fff' }}>
                {editingId ? 'Aggiorna' : 'Crea'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
